import React, { useState, useCallback } from 'react';
import { Footer } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import { useNavbarUser } from '../../utils/navbarUser';
import './SettingsPage.css';

/**
 * Settings page with four sections:
 * 1. Go Live! — streaming configuration
 * 2. Account — email, phone, password, 2FA
 * 3. Notify me if... — notification toggles
 * 4. Messaging — DM toggle + token price slider
 *
 * @returns {JSX.Element}
 */
function SettingsPage() {
  const user = useNavbarUser();

  /* Go Live! settings */
  const [goLiveBoxes, setGoLiveBoxes] = useState(3);
  const [messageDisplay, setMessageDisplay] = useState(true);
  const [videoMessageDisplay, setVideoMessageDisplay] = useState(true);

  /* Account settings */
  const [smsBackup, setSmsBackup] = useState(false);

  /* Notification settings */
  const [notifySettings, setNotifySettings] = useState({
    followGoLive: true,
    followAboutToLive: true,
    mention: true,
    directMessage: false,
  });

  /* Messaging settings */
  const [enableDMs, setEnableDMs] = useState(true);
  const [messagePrice, setMessagePrice] = useState(5000);

  const handleNotifyChange = useCallback((key) => {
    setNotifySettings((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleRowClick = useCallback((action) => {
    console.log('Settings action:', action);
  }, []);

  const formatTokenPrice = (value) => {
    return value.toLocaleString();
  };

  return (
    <div className="settings-page">
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={user.notifications}
        userAvatar={user.avatar}
        userName={user.name}
        onSpadesClick={() => {}}
        onLogout={() => {}}
      />

      <LeftSidebar />

      <main className="settings-page__main">
        <div className="settings-page__content">
          <h1 className="settings-page__title">Settings</h1>

          {/* ── Go Live! Section ───────────────────────────────── */}
          <SettingsSection title="Go Live!">
            <SettingsRow
              label="Test Video"
              onClick={() => handleRowClick('test-video')}
            />
            <SettingsRow
              label="Test Audio"
              onClick={() => handleRowClick('test-audio')}
            />
            <SettingsRow
              label="Go Live! Boxes"
              control={
                <select
                  className="settings-page__select"
                  value={goLiveBoxes}
                  onChange={(e) => setGoLiveBoxes(Number(e.target.value))}
                >
                  {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              }
              onClick={() => handleRowClick('go-live-boxes')}
            />
            <SettingsRow
              label="Message Display"
              control={
                <Toggle
                  checked={messageDisplay}
                  onChange={() => setMessageDisplay(!messageDisplay)}
                />
              }
              onClick={() => handleRowClick('message-display')}
            />
            <SettingsRow
              label="Video Message Display"
              control={
                <Toggle
                  checked={videoMessageDisplay}
                  onChange={() => setVideoMessageDisplay(!videoMessageDisplay)}
                />
              }
              onClick={() => handleRowClick('video-message-display')}
            />
            <SettingsRow
              label="Chat Display"
              onClick={() => handleRowClick('chat-display')}
            />
            <SettingsRow
              label="Social Display"
              onClick={() => handleRowClick('social-display')}
            />
            <SettingsRow
              label="Test Stream"
              onClick={() => handleRowClick('test-stream')}
            />
          </SettingsSection>

          {/* ── Account Section ────────────────────────────────── */}
          <SettingsSection title="Account">
            <SettingsRow
              label="Email"
              value="cookie@polarace.com"
              onClick={() => handleRowClick('email')}
            />
            <SettingsRow
              label="Phone"
              value="+1 (555) 123-4567"
              onClick={() => handleRowClick('phone')}
            />
            <SettingsRow
              label="Password"
              value="************"
              onClick={() => handleRowClick('password')}
            />
            <SettingsRow
              label="Add an Authenticator App"
              onClick={() => handleRowClick('authenticator')}
            />
            <SettingsRow
              label="SMS Backup Authentication"
              control={
                <Toggle
                  checked={smsBackup}
                  onChange={() => setSmsBackup(!smsBackup)}
                />
              }
              onClick={() => handleRowClick('sms-backup')}
            />
          </SettingsSection>

          {/* ── Notify me if... Section ────────────────────────── */}
          <SettingsSection title="Notify me if...">
            <SettingsRow
              label="Streamers you follow Go Live!"
              control={
                <Toggle
                  checked={notifySettings.followGoLive}
                  onChange={() => handleNotifyChange('followGoLive')}
                />
              }
            />
            <SettingsRow
              label="Streamers you follow are about to Go Live!"
              control={
                <Toggle
                  checked={notifySettings.followAboutToLive}
                  onChange={() => handleNotifyChange('followAboutToLive')}
                />
              }
            />
            <SettingsRow
              label="Streamer mentions you"
              control={
                <Toggle
                  checked={notifySettings.mention}
                  onChange={() => handleNotifyChange('mention')}
                />
              }
            />
            <SettingsRow
              label="You receive a Direct Message"
              control={
                <Toggle
                  checked={notifySettings.directMessage}
                  onChange={() => handleNotifyChange('directMessage')}
                />
              }
            />
          </SettingsSection>

          {/* ── Messaging Section ──────────────────────────────── */}
          <SettingsSection title="Messaging">
            <SettingsRow
              label="Enable DMs"
              control={
                <Toggle
                  checked={enableDMs}
                  onChange={() => setEnableDMs(!enableDMs)}
                />
              }
            />
            <div className="settings-page__slider-row">
              <div className="settings-page__slider-header">
                <span className="settings-page__slider-label">Message Price</span>
                <span className="settings-page__slider-value">
                  <span className="settings-page__slider-icon">&#9824;</span>
                  {formatTokenPrice(messagePrice)} Tokens
                </span>
              </div>
              <input
                type="range"
                className="settings-page__slider"
                min={2500}
                max={75000}
                step={500}
                value={messagePrice}
                onChange={(e) => setMessagePrice(Number(e.target.value))}
              />
              <div className="settings-page__slider-range">
                <span>2,500</span>
                <span>75,000</span>
              </div>
            </div>
          </SettingsSection>
        </div>

        <Footer />
      </main>
    </div>
  );
}

/**
 * Section wrapper with a title above a card container.
 */
function SettingsSection({ title, children }) {
  return (
    <section className="settings-page__section">
      <h2 className="settings-page__section-title">{title}</h2>
      <div className="settings-page__card">{children}</div>
    </section>
  );
}

/**
 * Individual settings row with label, optional value/control, and chevron arrow.
 */
function SettingsRow({ label, value, control, onClick }) {
  return (
    <div
      className={`settings-page__row ${onClick ? 'settings-page__row--clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
    >
      <span className="settings-page__row-label">{label}</span>
      <div className="settings-page__row-right">
        {value && <span className="settings-page__row-value">{value}</span>}
        {control && (
          <span
            className="settings-page__row-control"
            onClick={(e) => e.stopPropagation()}
          >
            {control}
          </span>
        )}
        {onClick && <span className="settings-page__row-arrow">&#8250;</span>}
      </div>
    </div>
  );
}

/**
 * Toggle switch component.
 */
function Toggle({ checked, onChange }) {
  return (
    <label className="settings-page__toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="settings-page__toggle-track" />
    </label>
  );
}

export default SettingsPage;
